'use client';

import { Icons } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useChaptersByProject } from '@/hooks/useChapters';
import { downloadFile } from '@/lib/utils';
import {
  ExportOptions,
  exportToDocx,
  exportToHtml,
  exportToMarkdown, // We'll keep using the raw one if needed or the service
  exportToText
} from '@/services/export/document-export';
import { getChapterContent } from '@/services/supabase/chapters';
import JSZip from 'jszip';
import { Download, Loader2 } from 'lucide-react';
import React, { useState } from 'react';

interface BulkChapterExportProps {
  projectId: string;
  projectName?: string;
  trigger?: React.ReactNode;
}

export function BulkChapterExport({ projectId, projectName, trigger }: BulkChapterExportProps) {
  const [open, setOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Default options similar to ExportDialog
  const [options, setOptions] = useState<ExportOptions>({
    title: projectName || 'Project Export',
    author: '', // Could be fetched from user meta if available in context
    format: 'docx',
    includeChapterTitles: true,
    includeTOC: false, // TOC might not make sense for individual files unless it's per chapter
    pageSize: 'A4',
    font: 'Georgia, serif',
    fontSize: 12,
    lineSpacing: 1.5,
    paragraphSpacing: 1.2,
    includePageNumbers: true,
  });

  const { data: chapters = [], isLoading } = useChaptersByProject(projectId);

  // Update title when project name is available
  React.useEffect(() => {
    if (projectName) {
      setOptions(prev => ({
        ...prev,
        title: projectName
      }));
    }
  }, [projectName]);

  const handleExport = async () => {
    if (!chapters.length) {
      // You might want to show a toast here
      console.error('No chapters found to export');
      return;
    }

    try {
      setIsExporting(true);

      // Create ZIP file
      const zip = new JSZip();

      // Process each chapter
      for (const chapter of chapters) {
        try {
          // Get chapter content
          const chapterContent = await getChapterContent(chapter.id);

          if (chapterContent && chapterContent.content && Array.isArray(chapterContent.content)) {
            const content = chapterContent.content;
            const filename = `${chapter.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`;

            // Export based on format using the service functions to ensure consistency with styling
            // We need to pass chapter-specific options (like title) to the export function if we want them inside the file
            // The service functions take 'options' which includes 'title'. 
            // We should arguably set the title to the CHAPTER title for the individual file export, 
            // or keep the Project title? 
            // Usually for individual chapter files, the file content might want the Chapter Title as the "Title".
            const chapterOptions = {
              ...options,
              title: chapter.title // Use chapter title for the individual document title
            };

            switch (options.format) {
              case 'docx':
                const docxBlob = await exportToDocx(content, chapterOptions);
                zip.file(`${filename}.docx`, docxBlob);
                break;

              case 'md': // 'markdown' in service, 'md' in local state. let's align to service?
                // Service uses 'markdown'.
                // We'll map 'md' locally or just use 'markdown' everywhere. 
                // Let's use 'markdown' in options state to match ExportDialog
                // But wait, the original used 'md'. I'll stick to 'markdown' as per ExportOptions interface.
                const mdContent = exportToMarkdown(content);
                // We might want to prepend title/author like in exportStory
                const fullMarkdown = `# ${chapterOptions.title}\n\n` +
                  (chapterOptions.author ? `By: ${chapterOptions.author}\n\n` : '') +
                  mdContent;
                zip.file(`${filename}.md`, fullMarkdown);
                break;

              case 'html':
                const htmlContent = exportToHtml(content);
                const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${chapterOptions.title}</title>
  <style>
    body { 
      font-family: ${options.font}; 
      font-size: ${options.fontSize}pt; 
      line-height: ${options.lineSpacing}; 
      max-width: 800px; 
      margin: 0 auto; 
      padding: 20px; 
    }
    p { margin-bottom: ${options.paragraphSpacing}em; }
  </style>
</head>
<body>
  <div style="text-align: center; font-size: 24pt; font-weight: bold; margin-bottom: 2em;">${chapterOptions.title}</div>
  ${options.author ? `<div style="text-align: center; font-size: 14pt; margin-bottom: 3em;">By ${options.author}</div>` : ''}
  ${htmlContent}
</body>
</html>`;
                zip.file(`${filename}.html`, fullHtml);
                break;

              case 'text':
                const textContent = exportToText(content);
                const fullText = `${chapterOptions.title.toUpperCase()}\n\n` +
                  (chapterOptions.author ? `By: ${chapterOptions.author}\n\n` : '') +
                  textContent;
                zip.file(`${filename}.txt`, fullText);
                break;

              case 'pdf':
                // PDF not implemented in bulk yet, skip or handle same as docx if service supports it (it relies on browser print mostly or backend)
                // Service has exportToPdf placehoder or similar? 
                // document-export.ts didn't show exportToPdf implementation returning a blob, it usually triggers print. 
                // Actually exportStory has `options.format === 'docx' || options.format === 'pdf'` logic but `exportToPdf` wasn't exported or implemented fully in the view I saw.
                // I will skip PDF for now or treat as DOCX if user selects it (but I won't offer it in the list if not ready)
                break;
            }
          }
        } catch (error) {
          console.error(`Error processing chapter ${chapter.title}:`, error);
          // Continue with other chapters
        }
      }

      // Generate and download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const timestamp = new Date().toISOString().split('T')[0];
      const zipFilename = `${(projectName || 'chapters').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_chapters_${timestamp}.zip`;

      downloadFile(zipBlob, zipFilename);
      setOpen(false);
    } catch (error) {
      console.error('Bulk export failed:', error);
      // user notification
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return null; // Or a loading spinner
  }

  const inputClass = "h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm";
  const labelClass = "text-sm font-medium text-foreground";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ? trigger : (
          <Button size="lg" className="w-full">
            <Download className="mr-2 h-5 w-5" />
            Export All Chapters
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
        <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
          <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">Export All Chapters</DialogTitle>
          <DialogDescription className="text-base text-muted-foreground leading-relaxed">
            Export all chapters from this project as individual files in a ZIP archive.
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6">
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6 bg-muted/30 p-1 rounded-xl">
              <TabsTrigger value="general" className="rounded-lg">General</TabsTrigger>
              <TabsTrigger value="format" className="rounded-lg">Format</TabsTrigger>
              <TabsTrigger value="preview" className="rounded-lg">Preview</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-6 mt-0">
              <div className="space-y-2.5">
                <Label htmlFor="author" className={labelClass}>
                  Author Name
                </Label>
                <Input
                  id="author"
                  value={options.author}
                  onChange={(e) =>
                    setOptions({ ...options, author: e.target.value })
                  }
                  placeholder="Enter author name"
                  className={inputClass}
                />
              </div>

              <div className="space-y-2.5">
                <Label htmlFor="format" className={labelClass}>
                  File Format
                </Label>
                <Select
                  value={options.format}
                  onValueChange={(value: any) =>
                    setOptions({ ...options, format: value })
                  }
                >
                  <SelectTrigger className={inputClass}>
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                    <SelectItem value="docx">Word Document (.docx)</SelectItem>
                    <SelectItem value="html">HTML Document (.html)</SelectItem>
                    <SelectItem value="markdown">Markdown (.md)</SelectItem>
                    <SelectItem value="text">Plain Text (.txt)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-2">
                <div className="flex items-center space-x-2 p-4 rounded-xl bg-muted/30 border border-muted-foreground/10">
                  <Checkbox
                    id="chapter-titles"
                    checked={options.includeChapterTitles}
                    onCheckedChange={(checked) =>
                      setOptions({
                        ...options,
                        includeChapterTitles: checked as boolean,
                      })
                    }
                  />
                  <Label htmlFor="chapter-titles" className="font-medium cursor-pointer">Include Chapter Titles in files</Label>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="format" className="space-y-6 mt-0">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2.5">
                  <Label htmlFor="pageSize" className={labelClass}>
                    Page Size
                  </Label>
                  <Select
                    value={options.pageSize}
                    onValueChange={(value: any) =>
                      setOptions({ ...options, pageSize: value })
                    }
                  >
                    <SelectTrigger className={inputClass}>
                      <SelectValue placeholder="Select page size" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                      <SelectItem value="A4">A4</SelectItem>
                      <SelectItem value="Letter">Letter</SelectItem>
                      <SelectItem value="Legal">Legal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2.5">
                  <Label htmlFor="fontSize" className={labelClass}>
                    Font Size
                  </Label>
                  <Select
                    value={options.fontSize?.toString()}
                    onValueChange={(value: any) =>
                      setOptions({ ...options, fontSize: Number(value) })
                    }
                  >
                    <SelectTrigger className={inputClass}>
                      <SelectValue placeholder="Select font size" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                      <SelectItem value="10">10pt</SelectItem>
                      <SelectItem value="11">11pt</SelectItem>
                      <SelectItem value="12">12pt</SelectItem>
                      <SelectItem value="14">14pt</SelectItem>
                      <SelectItem value="16">16pt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2.5">
                <Label htmlFor="font" className={labelClass}>
                  Font Family
                </Label>
                <Select
                  value={options.font}
                  onValueChange={(value: any) =>
                    setOptions({ ...options, font: value })
                  }
                >
                  <SelectTrigger className={inputClass}>
                    <SelectValue placeholder="Select font" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                    <SelectItem value="Georgia, serif">Georgia</SelectItem>
                    <SelectItem value='"Times New Roman", Times, serif'>
                      Times New Roman
                    </SelectItem>
                    <SelectItem value="Arial, sans-serif">Arial</SelectItem>
                    <SelectItem value="Verdana, sans-serif">Verdana</SelectItem>
                    <SelectItem value="monospace">Monospace</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2.5">
                  <Label htmlFor="lineSpacing" className={labelClass}>
                    Line Spacing
                  </Label>
                  <Select
                    value={options.lineSpacing?.toString()}
                    onValueChange={(value: any) =>
                      setOptions({ ...options, lineSpacing: Number(value) })
                    }
                  >
                    <SelectTrigger className={inputClass}>
                      <SelectValue placeholder="Select line spacing" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                      <SelectItem value="1">Single</SelectItem>
                      <SelectItem value="1.15">1.15</SelectItem>
                      <SelectItem value="1.5">1.5</SelectItem>
                      <SelectItem value="2">Double</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2.5">
                  <Label htmlFor="paragraphSpacing" className={labelClass}>
                    Paragraph Spacing
                  </Label>
                  <Select
                    value={options.paragraphSpacing?.toString()}
                    onValueChange={(value: any) =>
                      setOptions({ ...options, paragraphSpacing: Number(value) })
                    }
                  >
                    <SelectTrigger className={inputClass}>
                      <SelectValue placeholder="Select paragraph spacing" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                      <SelectItem value="0.8">Tight</SelectItem>
                      <SelectItem value="1">Normal</SelectItem>
                      <SelectItem value="1.2">Relaxed</SelectItem>
                      <SelectItem value="1.5">Loose</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="preview" className="mt-0">
              <div className="border border-border/40 rounded-xl p-8 h-[300px] overflow-auto bg-white text-black shadow-inner">
                <div
                  style={{
                    fontFamily: options.font,
                    fontSize: `${options.fontSize}pt`,
                    lineHeight: options.lineSpacing,
                  }}
                >
                  <div className="text-center font-bold text-2xl mb-6">
                    Chapter 1: The Beginning
                  </div>
                  {options.author && (
                    <div className="text-center mb-12 italic text-gray-600">
                      By {options.author}
                    </div>
                  )}
                  <p style={{ marginBottom: `${options.paragraphSpacing}em` }}>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed
                    non risus. Suspendisse lectus tortor, dignissim sit amet,
                    adipiscing nec, ultricies sed, dolor.
                  </p>
                  <p style={{ marginBottom: `${options.paragraphSpacing}em` }}>
                    Cras elementum ultrices diam. Maecenas ligula massa, varius
                    a, semper congue, euismod non, mi. Proin porttitor, orci
                    nec nonummy molestie, enim est eleifend mi, non fermentum
                    diam nisl sit amet erat.
                  </p>
                  <p style={{ marginBottom: `${options.paragraphSpacing}em` }}>
                    Duis semper. Duis arcu massa, scelerisque vitae, consequat
                    in, pretium a, enim. Pellentesque congue. Ut in risus
                    volutpat libero pharetra tempor. Cras vestibulum bibendum
                    augue.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="p-6 bg-muted/5 border-t border-border/40 flex-row gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setOpen(false)}
            className="min-w-[100px] rounded-xl hover:bg-muted/50"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleExport}
            disabled={isExporting || chapters.length === 0}
            className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base"
          >
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Icons.fileExport className="mr-2 h-4 w-4" />
                Export
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
